'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import api from '@/lib/api';
import {
  refreshAccessToken,
  persistAuthTokens,
  clearAuthTokens,
  scheduleProactiveRefresh,
} from '@/lib/authenticatedFetch';
import { HEARTBEAT_INTERVAL_MS } from '@/lib/adminPresence';
import { sanitizeRedirectPath } from '@/lib/postAuthRedirect';
import {
  canAccess,
  isAdminLikeRole,
  isSuperAdminRole,
} from '@/lib/adminPermissions';

export type AuthLoginOptions = {
  redirect?: string | null;
  inviteToken?: string | null;
};

export type AuthRegisterOptions = {
  inviteToken?: string | null;
};

interface User {
  id: string;
  email: string;
  role: string;
  email_verified: boolean;
  created_at: string;
  updated_at?: string;
  display_name?: string | null;
  permissions?: string[];
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string, options?: AuthLoginOptions) => Promise<string>;
  register: (
    email: string,
    password: string,
    role?: string,
    agreedToTerms?: boolean,
    agreedToPrivacy?: boolean,
    options?: AuthRegisterOptions
  ) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  isAdminLike: boolean;
  isSuperAdmin: boolean;
  can: (code: string | string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (!user) return;

    scheduleProactiveRefresh();

    const onVisible = () => {
      if (document.visibilityState === 'visible' && localStorage.getItem('refreshToken')) {
        void refreshAccessToken();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const ping = () => {
      if (document.visibilityState !== 'visible') return;
      void api.presence.heartbeat('web').catch(() => {});
    };

    ping();
    const intervalId = window.setInterval(ping, HEARTBEAT_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') ping();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user]);

  const loadUserFromSession = async (): Promise<boolean> => {
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('请求超时')), 5000);
    });
    const response = (await Promise.race([api.auth.getMe(), timeoutPromise])) as {
      user: User;
    };
    setUser(response.user);
    scheduleProactiveRefresh();
    return true;
  };

  const checkAuth = async () => {
    const hasRefresh = !!localStorage.getItem('refreshToken');
    const hasAccess = !!localStorage.getItem('accessToken');
    if (!hasAccess && !hasRefresh) {
      setLoading(false);
      return;
    }

    try {
      await loadUserFromSession();
    } catch {
      try {
        const renewed = await refreshAccessToken();
        if (renewed) {
          await loadUserFromSession();
        } else if (!localStorage.getItem('refreshToken')) {
          clearAuthTokens();
        }
      } catch {
        if (!localStorage.getItem('refreshToken')) {
          clearAuthTokens();
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string, options?: AuthLoginOptions) => {
    try {
      setLoading(true);
      const response = await api.auth.login({ email, password });

      persistAuthTokens({
        accessToken: response.tokens.accessToken,
        refreshToken: response.tokens.refreshToken,
        expiresIn: response.tokens.expiresIn,
      });
      setUser(response.user);
      setLoading(false);

      let target = sanitizeRedirectPath(options?.redirect);
      if (options?.inviteToken) {
        try {
          const acceptRes = await api.invites.accept(options.inviteToken);
          if (acceptRes?.redirect) {
            target = sanitizeRedirectPath(acceptRes.redirect);
          }
        } catch {
          /* 邀请接受失败不阻断登录 */
        }
      } else {
        try {
          const acceptRes = await api.invites.accept(null);
          if (acceptRes?.redirect) {
            target = sanitizeRedirectPath(acceptRes.redirect);
          }
        } catch {
          /* ignore */
        }
      }

      router.replace(target);
      return target;
    } catch (error: any) {
      setLoading(false);
      const errorMessage = error.message || '登录失败';
      const emailVerified = error.data?.email_verified;
      const enhancedError = new Error(errorMessage);
      (enhancedError as any).email_verified = emailVerified;
      (enhancedError as any).data = error.data;
      setError(errorMessage);
      throw enhancedError;
    }
  };

  const register = async (
    email: string,
    password: string,
    role = 'creator',
    agreedToTerms?: boolean,
    agreedToPrivacy?: boolean,
    options?: AuthRegisterOptions
  ) => {
    try {
      setLoading(true);
      const response = await api.auth.register({
        email,
        password,
        role,
        agreed_to_terms: agreedToTerms,
        agreed_to_privacy: agreedToPrivacy,
        invite_token: options?.inviteToken || undefined,
      });
      if (response.tokens) {
        persistAuthTokens({
          accessToken: response.tokens.accessToken,
          refreshToken: response.tokens.refreshToken,
          expiresIn: response.tokens.expiresIn,
        });
        setUser(response.user);
      }
    } catch (error: any) {
      const errorMessage = error.message || '注册失败';
      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    try {
      api.auth.logout();
    } catch (error) {
      setError(error instanceof Error ? error.message : '登出失败');
    } finally {
      clearAuthTokens();
      setUser(null);
      router.push('/');
    }
  };

  const refreshUser = async () => {
    try {
      const response = await api.auth.getMe();
      setUser(response.user);
    } catch (error) {
      setError(error instanceof Error ? error.message : '刷新用户信息失败');
    }
  };

  const isAdminLike = isAdminLikeRole(user?.role);
  const isSuperAdmin = isSuperAdminRole(user?.role);
  const can = useCallback(
    (code: string | string[]) => canAccess(user?.role, user?.permissions, code),
    [user?.role, user?.permissions]
  );

  const value: AuthContextType = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    refreshUser,
    isAdminLike,
    isSuperAdmin,
    can,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    return {
      user: null,
      loading: false,
      error: null,
      login: async () => { throw new Error('AuthProvider not available'); },
      register: async () => { throw new Error('AuthProvider not available'); },
      logout: () => {},
      refreshUser: async () => {},
      isAdminLike: false,
      isSuperAdmin: false,
      can: () => false,
    };
  }
  return context;
}
